
import { useLocation, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, AlertTriangle, Clock, Construction } from "lucide-react";
import { Button } from "@/components/ui/button";

const NotFound = () => {
  const location = useLocation();
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  
  useEffect(() => {
    // Check if the site is down completely (e.g., the main domain)
    const checkMaintenanceMode = () => {
      // If we're on the root domain with a 404, it's likely in maintenance mode
      const isRootDomain = window.location.pathname === "/" || 
                          !window.location.pathname || 
                          window.location.pathname === "/index.html";
      
      // Check for Netlify's specific error indicators in URL or document title
      const isNetlifyError = document.title.includes("Site not found") || 
                            window.location.href.includes("app.netlify.com/sites") ||
                            document.body.textContent?.includes("Netlify");
                            
      setIsMaintenanceMode(isRootDomain && isNetlifyError);
    };
    
    checkMaintenanceMode();
    
    console.error(
      isMaintenanceMode 
        ? "Site is in maintenance mode" 
        : `404 Error: User attempted to access non-existent route: ${location.pathname}`
    );
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white via-purple-50/30 to-blue-50/30 p-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
        {isMaintenanceMode ? (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
              <Construction className="h-8 w-8 text-blue-500" />
            </div>
            <h1 className="text-4xl font-bold mb-4 text-gray-900">Coming Soon</h1>
            <p className="text-xl text-gray-600 mb-6">
              We're currently performing some maintenance
            </p>
            <div className="flex items-center justify-center space-x-2 mb-8">
              <Clock className="h-5 w-5 text-blue-500" />
              <p className="text-gray-500">
                We'll be back shortly. Thank you for your patience!
              </p>
            </div>
            <img 
              src="/lovable-uploads/f99331ed-46d6-4407-9794-62b2335119bc.png" 
              alt="Coming Soon" 
              className="max-w-[200px] mx-auto mb-6"
            />
          </>
        ) : (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6">
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
            <h1 className="text-4xl font-bold mb-4 text-gray-900">404</h1>
            <p className="text-xl text-gray-600 mb-6">
              Oops! The page you're looking for cannot be found
            </p>
            <p className="text-gray-500 mb-8">
              The URL <span className="font-mono bg-gray-100 px-2 py-1 rounded text-sm">{location.pathname}</span> doesn't exist on this site.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="gap-2">
                <Link to="/">
                  <ArrowLeft className="h-4 w-4" />
                  Return Home
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <a href="mailto:support@edaptica.com">Contact Support</a>
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default NotFound;
